/**
 * Time tracking utilities
 */

import React from 'react';
import { IPatient, GetPatientsResponse } from './patient';
import localForage from 'localforage';
import { PostJSON } from './request';

// Constants

const localStore = localForage.createInstance({
  name: 'csTimeTracker'
});

const TRACK_TALK_TIMER_KEY = 'epx.timer';

// Types

interface SerializedTalkSession {
  userId: number;
  lastPolledAt: number; // ms from epoch, or 0 if not begun yet
  patientId: number; // patientId
  latestStart: number; // ms from epoch, or 0 if not begun yet
  accumulatedDuration: number;
  isStopped: boolean;
  note: string;
}

interface SplitDuration {
  minutes: number;
  seconds: number;
}

type Session = {
  userId: number;
  lastPolledAt?: number; // ms from epoch, or 0 if not begun yet
  patient?: IPatient;
  latestStart?: number; // ms from epoch, or 0 if not begun yet
  accumulatedDuration?: number;
  isStopped?: boolean;
  note?: string;
};

export class TalkSession {
  userId: number;
  lastPolledAt: number;
  patient: IPatient;
  latestStart: number; // ms from epoch, or 0 if not begun yet
  accumulatedDuration: number;
  isStopped: boolean;
  note: string;

  constructor({
    userId, // If reconstituting from local storage
    lastPolledAt = 0, // This is only > 0 if browser closed without stopping timer
    patient = null,
    latestStart = 0,
    accumulatedDuration = 0,
    isStopped = true,
    note = ''
  }: Session) {
    this.userId = userId;
    this.patient = patient;

    if (lastPolledAt && latestStart) {
      this.accumulatedDuration =
        accumulatedDuration + Math.floor((lastPolledAt - latestStart) / 1000);
      this.latestStart = Date.now();
      this.lastPolledAt = 0;
      this.isStopped = false;
    } else {
      this.latestStart = latestStart;
      this.isStopped = isStopped;
      this.accumulatedDuration = accumulatedDuration;
    }

    this.note = note;
  }

  serialize(): SerializedTalkSession {
    return {
      userId: this.userId,
      patientId: this.patient.id,
      latestStart: this.latestStart,
      accumulatedDuration: this.accumulatedDuration,
      isStopped: this.isStopped,
      note: this.note,
      lastPolledAt: this.lastPolledAt
    };
  }

  getLatestDuration(): number {
    if (!this.isStopped && this.latestStart > 0) {
      return Math.floor((Date.now() - this.latestStart) / 1000);
    } else {
      return 0;
    }
  }

  stop(): void {
    this.accumulatedDuration += this.getLatestDuration();
    this.latestStart = 0;
    this.isStopped = true;
  }

  start(): void {
    if (!this.patient) {
      throw new Error(
        'Cannot start a time tracking session without someone selected.'
      );
    }
    this.latestStart = Date.now();
    this.isStopped = false;
  }

  reset(): void {
    this.latestStart = 0;
    this.isStopped = true;
    this.accumulatedDuration = 0;
    this.note = '';
    this.lastPolledAt = 0;
  }

  get duration(): number {
    return this.accumulatedDuration + this.getLatestDuration();
  }

  get splitDuration(): SplitDuration {
    const minutes = Math.floor(this.duration / 60);
    const seconds = this.duration % 60;
    return {
      minutes,
      seconds
    };
  }
}

export class SessionManager {
  static async validate(
    loggedInUserId: number,
    session: TalkSession
  ): Promise<boolean> {
    if (!session || session.userId !== loggedInUserId) {
      try {
        await localStore.clear();
        return false; // Invalid session. User logged out before session completed.
      } catch {
        return false;
      }
    }
    return true;
  }

  // Saved patient has to be passed in, because we use a hook to load it.
  static async loadSession(
    loggedInUserId: number
  ): Promise<TalkSession | null> {
    try {
      const cachedSession: SerializedTalkSession =
        await localStore.getItem(TRACK_TALK_TIMER_KEY);
      if (!cachedSession?.userId) {
        return null;
      }
      const { userId, ...serializedSession } = cachedSession;
      const url = '/patient/data/';
      const response = await PostJSON<GetPatientsResponse, { id: number }>({
        url: url,
        params: { id: serializedSession.patientId }
      });
      const patients = (response as GetPatientsResponse).patients;
      const session = new TalkSession({
        ...serializedSession,
        userId,
        patient: patients[0]
      });

      if (SessionManager.validate(loggedInUserId, session)) {
        return session;
      } else {
        return null;
      }
    } catch {
      return null;
    }
  }

  static async clearSession(poll: number): Promise<void> {
    clearInterval(poll);
    await localStore.clear();
  }

  static async onPoll(
    session: TalkSession,
    setLastPolledAt: React.Dispatch<React.SetStateAction<number>>
  ): Promise<void> {
    const lastPolledAt = Date.now();
    session.lastPolledAt = lastPolledAt;
    setLastPolledAt(lastPolledAt);
    const serializedSession = session.serialize();
    await localStore.setItem(TRACK_TALK_TIMER_KEY, {
      ...serializedSession // If browser closes without session stop, this will let us reconstitute session
    });
  }

  static startPoll(
    session: TalkSession,
    setLastPolledAt: React.Dispatch<React.SetStateAction<number>>
  ): number {
    return window.setInterval(
      () => SessionManager.onPoll(session, setLastPolledAt),
      1000
    );
  }
}
